import React from 'react'
import clsx from 'clsx'
import { useLocation, useNavigate } from 'react-router-dom'
import { checkIsActive, WithChildren } from '../../../../helpers'
import { useSidebar } from '../Sidebar'

type Props = {
  to: string
  title: string
  icon?: string
  fontIcon?: string
  hasBullet?: boolean
  defaultPath?: string
  isOpen: boolean
  onToggle: () => void
}

const SidebarMenuItemWithSub: React.FC<Props & WithChildren> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  hasBullet,
  defaultPath,
  isOpen,
  onToggle,
}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { collapsed } = useSidebar()
  // Only highlight the parent when the sidebar is collapsed (children aren't visible).
  // When expanded, only the selected child link should be highlighted.
  const isActive = collapsed && checkIsActive(pathname, to)

  const handleToggle = () => {
    if (collapsed) {
      navigate(defaultPath || to)
      return
    }

    const willOpen = !isOpen
    onToggle()

    if (willOpen && defaultPath) {
      navigate(defaultPath)
    }
  }

  return (
    <div className='mb-1 rounded-lg overflow-hidden'>
      <button
        type='button'
        onClick={handleToggle}
        className={clsx(
          'relative w-full flex items-center rounded-lg text-xs font-normal transition-colors no-underline py-3',
          collapsed ? 'justify-center px-2' : 'gap-3 px-3 pr-8',
          isActive
            ? 'bg-white/10 text-white'
            : 'text-gray-400 hover:bg-white/10 hover:text-white'
        )}
        title={title}
      >
        {hasBullet && (
          <span className='w-1.5 h-1.5 rounded-full bg-current shrink-0' />
        )}
        {icon && (
          <img
            src={icon}
            className={clsx(
              'h-6 w-6 shrink-0 object-contain',
              isActive ? 'opacity-100' : 'opacity-60'
            )}
            height={24}
            width={24}
            alt=''
          />
        )}
        {fontIcon && <i className={clsx('bi fs-3', 'w-[52px] h-[52px]', fontIcon)} />}
        {!collapsed && (
          <span
            className={clsx(
              'text-left leading-tight text-[15px] font-medium',
              isActive ? 'text-white' : 'text-gray-400'
            )}
          >
            {title}
          </span>
        )}
        {!collapsed && <span className='flex-1' />}
        {!collapsed && (
          <svg
            className='w-4 h-4 shrink-0 absolute right-2 top-1/2 -translate-y-1/2'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d={isOpen ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
            />
          </svg>
        )}
      </button>
      {!collapsed && isOpen && <div className='mt-1 w-full px-2'>{children}</div>}
    </div>
  )
}

export { SidebarMenuItemWithSub }
